import { createContext, Suspense, useContext, useMemo, useState } from 'react'

import {
  CollectionDeleteModal,
  CollectionFormModal,
  CollectionOrganizationModal,
} from '@tribeplatform/react-components/Collection'
import { useCollection } from '@tribeplatform/react-sdk/hooks'

type CollectionMenuContextType = {
  openCollectionModal: (collectionId: string) => void
  openDeleteCollectionModal: (collectionId: string) => void
  openOrganizationModal: (collectionId: string) => void
}

export const CollectionMenuContext =
  createContext<CollectionMenuContextType | null>(null)

export const CollectionMenuProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [collectionId, setCollectionId] = useState<string | null>(null)
  const [openCollectionModal, setOpenCollectionModal] = useState(false)
  const [openDeleteCollectionModal, setOpenDeleteCollectionModal] =
    useState(false)
  const [openOrganizationModal, setOpenOrganizationModal] = useState(false)

  const {
    data: collection,
    isFetching,
    isPlaceholderData,
  } = useCollection({
    variables: {
      id: collectionId,
    },
    fields: {
      spaces: {
        fields: 'all',
        variables: {
          limit: 100,
        },
      },
    },
    useQueryOptions: {
      enabled: Boolean(collectionId),
    },
  })

  const loading = isFetching && isPlaceholderData
  const spaces = collection?.spaces?.nodes ?? []

  const value = useMemo(
    () => ({
      openCollectionModal: (collectionId: string) => {
        setCollectionId(collectionId)
        setOpenCollectionModal(true)
      },
      openDeleteCollectionModal: (collectionId: string) => {
        setCollectionId(collectionId)
        setOpenDeleteCollectionModal(true)
      },
      openOrganizationModal: (collectionId: string) => {
        setCollectionId(collectionId)
        setOpenOrganizationModal(true)
      },
    }),
    [
      setOpenCollectionModal,
      setOpenDeleteCollectionModal,
      setOpenOrganizationModal,
    ],
  )

  return (
    <CollectionMenuContext.Provider value={value}>
      {children}
      {collection && (
        <>
          <Suspense fallback={<></>}>
            <CollectionFormModal
              open={openCollectionModal}
              id={collection.id}
              name={collection.name}
              description={collection.description}
              onClose={() => {
                setOpenCollectionModal(false)
                setCollectionId(null)
              }}
            />
          </Suspense>

          <Suspense fallback={<></>}>
            <CollectionDeleteModal
              open={openDeleteCollectionModal}
              onClose={() => {
                setOpenDeleteCollectionModal(false)
                setCollectionId(null)
              }}
              id={collection.id}
              name={collection.name}
              spaces={spaces}
              loading={loading}
              skipNavigate
            />
          </Suspense>
          <Suspense fallback={<></>}>
            <CollectionOrganizationModal
              open={openOrganizationModal}
              onClose={() => {
                setOpenOrganizationModal(false)
                setCollectionId(null)
              }}
              id={collection.id}
              spaces={spaces}
              loading={loading}
            />
          </Suspense>
        </>
      )}
    </CollectionMenuContext.Provider>
  )
}

export function useCollectionMenu() {
  const context = useContext(CollectionMenuContext)
  if (!context) {
    throw new Error(
      'useCollectionMenu must be used within a CollectionMenuProvider',
    )
  }
  return context
}
